<template>
  <v-container fluid>
    <section>
      <!-- Summary Cards (4 Cards) -->
      <KpiCards class="mb-3" :items="kpiItems" :loading="loadingSummary" :skeleton="4" />

      <data-table
        :headers="headers"
        :items="productSizes"
        commit="SET_PRODUCT_SIZE"
        :viewItem="viewItem"
        :editItem="editItem"
        :deleteItem="deleteItem"
      >
        <div slot="activator" class="row justify-content-between mb-4">
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-3">
            <v-card-title
              class="px-0 pt-0 ps-products-title"
              :class="{ 'white--text': $vuetify.theme.dark }"
            >
              {{ $t("manage_product_unit") }}
            </v-card-title>
          </div>
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-3 offset-lg-4">
            <TextSearch
              v-model="search"
              @search="fetchItem()"
              @onClear="
                search = '';
                fetchItem();
              "
            />
          </div>
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-2 text-end">
            <v-btn
              depressed
              color="primary"
              class="white--text btn-primary-brand"
              @click="newItem"
            >
              <v-icon left small>fal fa-plus</v-icon>
              {{ $t("new_data") }}
            </v-btn>
          </div>
        </div>
      </data-table>

      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="description"
        @submit="submitForm"
      >
        <div slot="activator">
          <v-row>
            <v-col cols="4">{{ $t("id") }}</v-col>
            <v-col cols="8">{{ form.id }}</v-col>
            <v-col cols="4">{{ $t("name") }}</v-col>
            <v-col cols="8">{{ form.name }}</v-col>
            <v-col cols="4">{{ $t("name_en") }}</v-col>
            <v-col cols="8">{{ form.name_en }}</v-col>
            <v-col cols="4">{{ $t("name_th") }}</v-col>
            <v-col cols="8">{{ form.name_th }}</v-col>
            <v-col cols="4">{{ $t("name_cn") }}</v-col>
            <v-col cols="8">{{ form.name_cn }}</v-col>
            <v-col cols="4">{{ $t("date") }}</v-col>
            <v-col cols="8">{{ form.created_at | moment }}</v-col>
          </v-row>
        </div>
      </app-custom-dialog>
      <app-custom-dialog
        v-model="dialogAdd"
        :footer="true"
        :title="titleStatus"
        :submit-text="update ? 'save' : 'add'"
        :submit-loading="submitting"
        @submit="submitForm"
      >
        <div slot="activator">
          <TTextField
            label="name"
            v-model="form.name"
            :errorMessages="nameError"
            @input="$v.form.name.$touch()"
            @blur="$v.form.name.$touch()"
          />
          <TTextField label="name_en" class="mt-3" v-model="form.name_en" />
          <TTextField label="name_th" class="mt-3" v-model="form.name_th" />
          <TTextField label="name_cn" class="mt-3" v-model="form.name_cn" />

          <p class="pt-3 pb-0 font-weight-medium">{{ $t("unit") }}</p>
          <v-radio-group v-model="form.unit" color="primary">
            <v-radio
              v-for="n in sizeType"
              :key="n"
              :label="namType(n)"
              :value="n"
              color="primary"
            ></v-radio>
          </v-radio-group>

          <TTextField
            v-if="form.unit === 'pack'"
            label="pack"
            typeShow="number"
            v-model="form.amount"
            :errorMessages="amountError"
            @input="$v.form.amount.$touch()"
            @blur="$v.form.amount.$touch()"
          />
        </div>
      </app-custom-dialog>
      <!-- AppLoading -->
      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import DataTable from "@/components/DataTable";
import { mapGetters, mapActions } from "vuex";
import { productSizeHead } from "@/utils/head";
import TTextField from "@/components/global/TextField";
import { sizeValidate } from "@/validations/shop";
import { success, confirm, toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";

const defaultUnitForm = () => ({
  name: "",
  name_en: "",
  name_th: "",
  name_cn: "",
  unit: "amount",
  amount: 1,
});

export default {
  mixins: [sizeValidate, myMixin],
  data: () => ({
    update: false,
    dialogAdd: false,
    dialog: false,
    overlay: false,
    submitting: false,
    search: "",
    loadingSummary: true,
    summaries: [],

    sizeType: ["amount", "pack", "dynamic"],
    form: defaultUnitForm(),
  }),
  async created() {
    await Promise.all([this.fetchItem(), this.fetchSummary()]);
  },
  components: { DataTable, TTextField },
  watch: {
    form: {
      deep: true,
      handler(val) {
        if (val.unit === "pack") {
          if (val.amount === 0) {
            this.$set(this.form, "amount", null);
          }
        } else {
          this.$set(this.form, "amount", 1);
        }
      },
    },
  },
  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => ({
        key: index,
        title: item.title,
        icon: item.icon,
        value: item.total ?? 0,
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      productSizes: "index/productSizes",
    }),

    titleStatus() {
      return this.update ? "update" : "new_data";
    },
    headers() {
      return productSizeHead(this);
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    namType(n) {
      if (n === "amount") {
        return this.$t("amount");
      } else if (n === "pack") {
        return this.$t("pack");
      } else if (n === "dynamic") {
        return this.$t("dynamic");
      } else {
        return "";
      }
    },
    async fetchItem() {
      const body = {
        that: this,
        url: `product_size_paginate?q=${this.search}`,
        commit: "SET_PRODUCT_SIZE",
      };
      await this.fetch(body);
    },

    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const { data } = await this.$http.get("get_summary_units");
        if (data && data.status) {
          this.summaries = data.data;
        }
      } catch (error) {
        console.log("error fetching unit summary", error);
      } finally {
        this.loadingSummary = false;
      }
    },

    newItem() {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.form = defaultUnitForm();
      this.update = false;
      this.dialogAdd = true;
    },
    editItem(item) {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.update = true;
      this.form = {
        ...defaultUnitForm(),
        ...item,
      };
      this.dialogAdd = true;
    },
    async deleteItem({ id }) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        try {
          const { data } = await this.$http.delete(`product_size/${id}`);
          if (data && data.status) {
            await this.fetchItem();
            await this.fetchSummary();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("error_occurred") ||
            "Error";
          if (this.$toast) {
            this.$toast.error(message);
          } else {
            await toast(this, message, "error");
          }
        }
      }
    },
    viewItem(item) {
      this.form = {
        ...defaultUnitForm(),
        ...item,
      };
      this.dialog = true;
    },
    async submitForm() {
      try {
        this.$v.form.$touch();
        if (this.$v.form.$invalid) {
          return;
        }

        this.submitting = true;
        if (this.update) {
          const { data } = await this.$http.put(
            `product_size/${this.form.id}`,
            this.form
          );
          if (data && data.status) {
            this.dialogAdd = false;
            await this.fetchItem();
            await this.fetchSummary();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        } else {
          const { data } = await this.$http.post("product_size", this.form);
          if (data && data.status) {
            this.dialogAdd = false;
            await this.fetchItem();
            await this.fetchSummary();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        }
      } catch (error) {
        console.error("error submitting unit", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("error_occurred") ||
          "Error";
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          await toast(this, message, "error");
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.ps-products-title {
  font-size: 1.25rem;
  font-weight: 900;
  color: #141021;
}

.btn-primary-brand {
  background-color: #5b39c9 !important;
  color: #ffffff !important;
  border-radius: 8px !important;
  font-weight: 700 !important;
}

.btn-primary-brand:hover {
  background-color: #4f2fb9 !important;
}
</style>
